import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Grid,
  TextField,
  Button,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Functions as FunctionsIcon
} from '@mui/icons-material';

// eslint-disable-next-line max-lines-per-function
const ChartConfigModal = ({ open, onClose, chartConfig, onSave }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [config, setConfig] = useState({
    chartType: 'bar',
    chartOptions: {
      title: '',
      xLabel: '',
      yLabel: '',
      width: '100%',
      height: 400,
      colors: ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'],
      showLegend: true,
      showTooltip: true,
      innerRadius: 0,
      padding: 0.1
    },
    chartData: []
  });

  const [newDataRow, setNewDataRow] = useState({ label: '', value: 0, x: '', y: '' });
  const [newColor, setNewColor] = useState('#000000');

  useEffect(() => {
    if (chartConfig) {
      setConfig({
        chartType: chartConfig.chartType || 'bar',
        chartOptions: { ...config.chartOptions, ...chartConfig.chartOptions },
        chartData: chartConfig.chartData || []
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartConfig]);

  const handleAddDataRow = () => {
    const dataRow = config.chartType === 'heatmap'
      ? { x: newDataRow.x, y: newDataRow.y, value: parseFloat(newDataRow.value) || 0 }
      : { label: newDataRow.label, value: parseFloat(newDataRow.value) || 0 };

    setConfig({
      ...config,
      chartData: [...config.chartData, dataRow]
    });
    setNewDataRow({ label: '', value: 0, x: '', y: '' });
  };

  const handleDeleteDataRow = (index) => {
    setConfig({
      ...config,
      chartData: config.chartData.filter((_, i) => i !== index)
    });
  };

  const handleAddColor = () => {
    if (!config.chartOptions.colors.includes(newColor)) {
      setConfig({
        ...config,
        chartOptions: {
          ...config.chartOptions,
          colors: [...config.chartOptions.colors, newColor]
        }
      });
    }
  };

  const handleDeleteColor = (index) => {
    setConfig({
      ...config,
      chartOptions: {
        ...config.chartOptions,
        colors: config.chartOptions.colors.filter((_, i) => i !== index)
      }
    });
  };

  const handleSave = () => {
    onSave(config);
    onClose();
  };

  // eslint-disable-next-line max-lines-per-function
  const renderGeneralTab = () => (
    <Box sx={{ pt: 2 }}>
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            fullWidth
            label="Chart Title"
            value={config.chartOptions.title}
            onChange={(e) => setConfig({
              ...config,
              chartOptions: { ...config.chartOptions, title: e.target.value }
            })}
            placeholder="e.g., Monthly Revenue {{year}}"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title="You can use variables">
                    <FunctionsIcon color="action" />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            fullWidth
            label="X-Axis Label"
            value={config.chartOptions.xLabel}
            onChange={(e) => setConfig({
              ...config,
              chartOptions: { ...config.chartOptions, xLabel: e.target.value }
            })}
            placeholder="e.g., {{x_axis_label}}"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            fullWidth
            label="Y-Axis Label"
            value={config.chartOptions.yLabel}
            onChange={(e) => setConfig({
              ...config,
              chartOptions: { ...config.chartOptions, yLabel: e.target.value }
            })}
            placeholder="e.g., {{y_axis_label}}"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            fullWidth
            label="Width"
            value={config.chartOptions.width}
            onChange={(e) => setConfig({
              ...config,
              chartOptions: { ...config.chartOptions, width: e.target.value }
            })}
            helperText="Use % for responsive or px for fixed"
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6
          }}>
          <TextField
            fullWidth
            label="Height (px)"
            type="number"
            value={config.chartOptions.height}
            onChange={(e) => setConfig({
              ...config,
              chartOptions: { ...config.chartOptions, height: parseInt(e.target.value) || 400 }
            })}
          />
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={config.chartOptions.showLegend}
                onChange={(e) => setConfig({
                  ...config,
                  chartOptions: { ...config.chartOptions, showLegend: e.target.checked }
                })}
              />
            }
            label="Show Legend"
          />
          <FormControlLabel
            control={
              <Switch
                checked={config.chartOptions.showTooltip}
                onChange={(e) => setConfig({
                  ...config,
                  chartOptions: { ...config.chartOptions, showTooltip: e.target.checked }
                })}
              />
            }
            label="Show Tooltips"
          />
        </Grid>

        {(config.chartType === 'donut') && (
          <Grid size={12}>
            <TextField
              fullWidth
              label="Inner Radius (0-1)"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.1 }}
              value={config.chartOptions.innerRadius}
              onChange={(e) => setConfig({
                ...config,
                chartOptions: { ...config.chartOptions, innerRadius: parseFloat(e.target.value) || 0 }
              })}
              helperText="0 = Pie chart, 0.5 = Standard donut"
            />
          </Grid>
        )}

        {config.chartType === 'bar' && (
          <Grid size={12}>
            <TextField
              fullWidth
              label="Bar Padding (0-1)"
              type="number"
              inputProps={{ min: 0, max: 1, step: 0.05 }}
              value={config.chartOptions.padding}
              onChange={(e) => setConfig({
                ...config,
                chartOptions: { ...config.chartOptions, padding: parseFloat(e.target.value) || 0.1 }
              })}
              helperText="Space between bars"
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );

  // eslint-disable-next-line max-lines-per-function
  const renderDataTab = () => (
    <Box sx={{ pt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Chart Data
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        You can use static values or template variables like {`{{revenue_q1}}`}
      </Typography>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {config.chartType === 'heatmap' ? (
                <>
                  <TableCell>X Value</TableCell>
                  <TableCell>Y Value</TableCell>
                  <TableCell>Value</TableCell>
                </>
              ) : (
                <>
                  <TableCell>Label</TableCell>
                  <TableCell>Value</TableCell>
                </>
              )}
              <TableCell width={50}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {config.chartData.map((row, index) => (
              <TableRow key={index}>
                {config.chartType === 'heatmap' ? (
                  <>
                    <TableCell>{row.x}</TableCell>
                    <TableCell>{row.y}</TableCell>
                    <TableCell>{row.value}</TableCell>
                  </>
                ) : (
                  <>
                    <TableCell>{row.label}</TableCell>
                    <TableCell>{row.value}</TableCell>
                  </>
                )}
                <TableCell>
                  <IconButton size="small" onClick={() => handleDeleteDataRow(index)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
        {config.chartType === 'heatmap' ? (
          <>
            <TextField
              size="small"
              label="X Value"
              value={newDataRow.x}
              onChange={(e) => setNewDataRow({ ...newDataRow, x: e.target.value })}
              placeholder="e.g., Monday or {{day}}"
            />
            <TextField
              size="small"
              label="Y Value"
              value={newDataRow.y}
              onChange={(e) => setNewDataRow({ ...newDataRow, y: e.target.value })}
              placeholder="e.g., Morning or {{period}}"
            />
          </>
        ) : (
          <TextField
            size="small"
            label="Label"
            value={newDataRow.label}
            onChange={(e) => setNewDataRow({ ...newDataRow, label: e.target.value })}
            placeholder="e.g., Q1 or {{quarter_1}}"
            sx={{ flex: 1 }}
          />
        )}
        <TextField
          size="small"
          label="Value"
          type="text"
          value={newDataRow.value}
          onChange={(e) => setNewDataRow({ ...newDataRow, value: e.target.value })}
          placeholder="e.g., 100 or {{revenue_q1}}"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddDataRow}
          disabled={config.chartType === 'heatmap' ? !newDataRow.x || !newDataRow.y : !newDataRow.label}
        >
          Add
        </Button>
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          <strong>Pro tip:</strong> Use variables like {`{{quarter_revenue}}`} in values, and they'll be replaced with actual data when generating reports. The AI will understand the context and populate appropriate values.
        </Typography>
      </Alert>
    </Box>
  );

  const renderStyleTab = () => (
    <Box sx={{ pt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        Chart Colors
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {config.chartOptions.colors.map((color, index) => (
          <Box
            key={index}
            sx={{
              position: 'relative',
              width: 50,
              height: 50,
              backgroundColor: color,
              border: '2px solid',
              borderColor: 'divider',
              borderRadius: 1,
              cursor: 'pointer',
              '&:hover .delete-btn': {
                opacity: 1
              }
            }}
          >
            <IconButton
              className="delete-btn"
              size="small"
              sx={{
                position: 'absolute',
                top: -8,
                right: -8,
                backgroundColor: 'background.paper',
                opacity: 0,
                transition: 'opacity 0.2s',
                '&:hover': {
                  backgroundColor: 'error.light'
                }
              }}
              onClick={() => handleDeleteColor(index)}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          type="color"
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          sx={{ width: 80 }}
        />
        <TextField
          value={newColor}
          onChange={(e) => setNewColor(e.target.value)}
          placeholder="#000000"
          size="small"
        />
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddColor}
        >
          Add Color
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Colors will be applied to data points in order. Add more colors for better distinction in charts with many data points.
      </Typography>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Configure Chart</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab label="General" />
          <Tab label="Data" />
          <Tab label="Style" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {activeTab === 0 && renderGeneralTab()}
          {activeTab === 1 && renderDataTab()}
          {activeTab === 2 && renderStyleTab()}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={config.chartData.length === 0}
        >
          Apply Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChartConfigModal;